/**
 * 1005 登录会话滑动续期 —— console 侧。
 *
 * 后端(api JwtFilter)对活跃会话:token 签发满 10 分钟后的下一次正常请求,在响应头 `X-Renewed-Token`
 * 下发一张**同一会话**(同 sid、claims 原样)但 exp 顺延满 24h 的新 token。这里负责把它换进 localStorage。
 * 企微 H5 的 token 在 HttpOnly cookie 里,后端直接 Set-Cookie 续,JS 不经手(也读不到)。
 *
 * 换票规则(防并发 / 多标签页打架):
 * - 只在本地**还有票**且新票与本地票是**同一会话**(sub / scope / sid / tenantCode / partnerCode 全等,
 *   且两边都不是代客票)时才换 —— 登出后迟到的响应、换了账号、代客中途回来的旧响应,都不会把票写回去;
 * - 只换 **exp 更晚** 的 —— 并发请求各拿到一张续期票、乱序回来时,先到的晚票不会被后到的早票覆盖;
 * - 同时看代客暂存的原始票(`xbgeo-origin-token`):代客期间它不发请求,一般不会命中,命中时同规则。
 * 同会话的新旧票后端都认(同 sid),所以别的标签页手里还拿着旧票的在途请求不会被判踢。
 */

export const RENEWED_TOKEN_HEADER = "X-Renewed-Token";

// 与 httpClient TOKEN_KEY / ImpersonationContext ORIGIN_TOKEN_KEY 同一字面量。
const TOKEN_KEYS = ["xbgeo-token", "xbgeo-origin-token"] as const;

interface SessionClaims {
  sub?: string;
  scope?: string;
  sid?: string;
  tenantCode?: string;
  partnerCode?: string;
  actAsTenant?: string;
  exp?: number;
}

export function decodeSessionClaims(token: string | null | undefined): SessionClaims | null {
  if (!token) return null;
  const part = token.split(".")[1];
  if (!part) return null;
  try {
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const bin = atob(padded);
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    const json = new TextDecoder().decode(bytes);
    const obj = JSON.parse(json) as unknown;
    return obj && typeof obj === "object" ? (obj as SessionClaims) : null;
  } catch {
    return null;
  }
}

function sameSession(a: SessionClaims, b: SessionClaims): boolean {
  if (a.actAsTenant || b.actAsTenant) return false;
  return (
    a.sub === b.sub &&
    a.scope === b.scope &&
    (a.sid ?? null) === (b.sid ?? null) &&
    (a.tenantCode ?? null) === (b.tenantCode ?? null) &&
    (a.partnerCode ?? null) === (b.partnerCode ?? null)
  );
}

/**
 * 读响应头里的续期票,按上面的规则换进 localStorage。返回是否换了(给测试看)。
 * 任何异常都吞掉:续期是锦上添花,失败就继续用旧票。
 */
export function adoptRenewedToken(res: Pick<Response, "headers"> | null | undefined): boolean {
  try {
    const fresh = res?.headers?.get?.(RENEWED_TOKEN_HEADER);
    if (!fresh) return false;
    const next = decodeSessionClaims(fresh);
    if (!next || typeof next.exp !== "number" || next.actAsTenant) return false;
    let adopted = false;
    for (const key of TOKEN_KEYS) {
      const cur = localStorage.getItem(key);
      if (!cur || cur === fresh) continue;
      const c = decodeSessionClaims(cur);
      if (!c || !sameSession(c, next)) continue;
      if (typeof c.exp === "number" && next.exp <= c.exp) continue;
      localStorage.setItem(key, fresh);
      adopted = true;
    }
    return adopted;
  } catch {
    return false;
  }
}
